<template>
  <v-btn
    :class="[
      `VcButton ${flavor}__text`,
      { 'white-text': white },
      { 'hide-disabled-background': !showDisabledBackground },
    ]"
    :color="flavor ? buttonColor : color"
    :icon="icon"
    :fab="fab"
    :outlined="outlined"
    :text="ghost"
    :rounded="pill"
    :plain="plain"
    :x-small="xSmall"
    :small="small"
    :large="large"
    :x-large="xLarge"
    :ripple="ripple"
    :elevation="elevation"
    :width="width"
    min-width="0"
    :loading="loading"
    :block="block"
    :disabled="disabled"
    :href="href"
    :data-qa="dataQa"
    @click="(event) => $emit('click', event)">
    <slot name="prepend">
      <VcIcon
        v-if="prependDefaultIcon"
        color="currentColor"
        :class="{ prependDefaultIcon: !icon }"
        :size="iconSize">
        {{ getPrependIcon }}
      </VcIcon>
    </slot>
    <slot>
      {{ label || ($slots.default && $slots.default[0].text) || '' }}
    </slot>
    <slot name="append" />
  </v-btn>
</template>

<script>
import VcIcon from '../VcIcon/VcIcon.vue';

export default {
  name: 'VcButton',
  components: { VcIcon },
  props: {
    /**
     * Text for display on the button
     */
    label: {
      type: String,
      required: false,
    },
    plain: {
      type: Boolean,
      default: false,
    },
    elevation: {
      type: Number,
      default: 0,
    },
    /**
     * Shortcut for setting the button size
     */
    xSmall: {
      type: Boolean,
      default: false,
    },
    /**
     * Shortcut for setting the button size
     */
    small: {
      type: Boolean,
      default: false,
    },
    /**
     * Shortcut for setting the button size
     */
    large: {
      type: Boolean,
      default: false,
    },
    /**
     * Shortcut for setting the button size
     */
    xLarge: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: 'secondary',
      validator: (type) => {
        return ['secondary', 'white', 'black', 'ai'].indexOf(type) > -1;
      },
    },
    flavor: {
      type: String,
      default: 'primary',
      validator: (type) => ['secondary', 'primary', 'ai'].includes(type),
    },
    /**
     * white color text and icon in case of dark background
     */
    white: {
      type: Boolean,
      default: false,
    },
    ghost: {
      type: Boolean,
      default: false,
    },
    /**
     * The button will contain an icon and no text
     */
    icon: {
      type: Boolean,
      default: false,
    },
    fab: {
      type: Boolean,
      default: false,
    },
    pill: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showDisabledBackground: {
      type: Boolean,
      default: true,
    },
    width: {
      type: Number,
      required: false,
    },
    ripple: {
      type: Boolean,
    },
    href: {
      type: String,
      comment: 'This param will change the <button> to <a> with link',
    },
    prependDefaultIcon: {
      type: Boolean,
      default: false,
    },
    prependIcon: {
      type: String,
      default: 'plus',
    },
    iconSize: {
      type: Number,
      default: 16,
    },
    dataQa: {
      type: String,
      default: 'vc-btn',
    },
  },
  computed: {
    buttonColor() {
      switch (this.flavor) {
        case 'primary': // When the flavor is primary, the theme color is the value in secondary color
          return this.color;
        case 'secondary':
          return this.outlined ? '--gray-darken-5' : '--neutral-lighten-2';
        default:
          console.error(
            'ButtonColor could not be determined since the flavour is not expected, flavor is: ' +
              this.flavor
          );
          return '';
      }
    },
    getPrependIcon() {
      return `$${this.prependIcon}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.VcButton {
  letter-spacing: var(--letter-spacing);

  &:disabled {
    background-color: var(--gray-lighten-2);
    color: var(--gray-darken-2);
  }

  &.hide-disabled-background {
    &:disabled {
      color: var(--gray-lighten-2) !important;
      background-color: transparent;
    }
  }

  .prependDefaultIcon {
    margin-inline-end: var(--size-value3);
  }

  &.secondary__text {
    color: var(--gray-darken-5) !important;
    border-color: var(--neutral-lighten-1);
  }

  &.ai__text:not(.v-btn--disabled) {
    color: var(--ai-main) !important;
  }

  &.white-text {
    color: var(--gray-lighten-4) !important;
  }
}
</style>
